'use client';

/* eslint-disable @next/next/no-img-element -- A sportsbook logo is a local asset (mostly SVG, which next/image serves
   unoptimized); a plain img costs nothing and no image-optimization usage. */
import { memo } from 'react';
import { cn, useBook } from '@sharpr/ui';
import type { BookId } from '@sharpr/types';
import { presentationFor } from '@sharpr/ui/books';

interface BookThumbnailProps {
  book: BookId;
  size?: 'sm' | 'md';
  className?: string;
}

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-4 h-4 sm:w-5 sm:h-5',
};

const fallbackText = {
  sm: 'text-[8px]',
  md: 'text-[7px] sm:text-[8px]',
};

export const BookThumbnail = memo(function BookThumbnail({ book, size = 'md', className }: BookThumbnailProps) {
  const { name } = useBook(book);
  const presentation = presentationFor(book, name);
  if (presentation.thumbnail) {
    return (
      <img
        src={presentation.thumbnail}
        alt={name}
        className={cn(
          'rounded object-cover flex-shrink-0',
          sizeClasses[size],
          presentation.thumbnailScale,
          className
        )}
        loading="lazy"
      />
    );
  }
  return (
    <div
      className={cn(
        'rounded bg-muted dark:bg-bg-secondary flex items-center justify-center flex-shrink-0',
        sizeClasses[size],
        className
      )}
    >
      <span className={cn('font-bold text-muted-foreground dark:text-text-tertiary', fallbackText[size])}>
        {presentation.abbreviation}
      </span>
    </div>
  );
});

interface BookLabelProps {
  book: BookId;
  className?: string;
  size?: 'sm' | 'md';
  /** Bump the book name to semibold — used to emphasize the best-value row. */
  strong?: boolean;
}

/** A book's display name from the catalog (the id until the catalog loads). */
export function BookName({ book }: { book: BookId }) {
  return <>{useBook(book).name}</>;
}

export const BookLabel = memo(function BookLabel({ book, className, size = 'md', strong = false }: BookLabelProps) {
  return (
    <div className={cn('flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0', className)}>
      <BookThumbnail book={book} size={size} />
      <span className={cn(
        'text-[11px] sm:text-xs truncate text-left text-gray-700 dark:text-text-primary',
        strong ? 'font-semibold' : 'font-medium'
      )}>
        <BookName book={book} />
      </span>
    </div>
  );
});
